import { ReactComponent as ExcelIcon } from "../../assets/images/icons/ExcelIcon.svg";
import { ReactComponent as GalleryIcon } from "../../assets/images/icons/GalleryIcon.svg";
import { ReactComponent as ListViewIcon } from "../../assets/images/icons/ListViewIcon.svg";
import { ReactComponent as OfficeAccessIcon } from "../../assets/images/icons/OfficeAccessIcon.svg";
import { ReactComponent as OfficeDocIcon } from "../../assets/images/icons/OfficeDocIcon.svg";
import { ReactComponent as OfficeOutlookIcon } from "../../assets/images/icons/OfficeOutlookIcon.svg";
import { ReactComponent as PdfIcon } from "../../assets/images/icons/PdfIcon.svg";
import { ReactComponent as PptIcon } from "../../assets/images/icons/PptIcon.svg";
import { ReactComponent as FolderIcon } from "../../assets/images/icons/FolderIcon.svg";
import { ReactComponent as OfficePublisherIcon } from "../../assets/images/icons/OfficePublisherIcon.svg";

const DOCUMENT_LIST_ICON = [
  {
    value: ["folder", "subfolder"],
    icon: FolderIcon,
  },
  {
    value: ["file", "doc", "docx", "odt", "rtf", "txt", "wps", "word", "wri", "sdw", "sgl", "dot", "dotx"],
    icon: OfficeDocIcon,
  },
  {
    value: [
      "file",
      "image",
      "jpg",
      "jpeg",
      "png",
      "webp",
      "gif",
      "svg",
      "bmp",
      "tiff",
      "tif",
      "ico",
      "heif",
      "heic",
      "jfif",
      "raw",
      "indd",
      "ai",
      "eps",
      "psd",
    ],
    icon: GalleryIcon,
  },
  {
    value: ["file", "xlsx", "xls", "csv", "ods", "xlsm", "xlt", "xltx", "tsv", "slk", "dif", "sdc"],
    icon: ExcelIcon,
  },
  {
    value: ["file", "ppt", "pptx", "odp", "pps", "ppsx", "pot", "potx", "sda", "sdd"],
    icon: PptIcon,
  },
  {
    value: ["file", "msg", "eml", "ost", "pst"],
    icon: OfficeOutlookIcon,
  },
  {
    value: ["file", "mdb", "accdb", "mde", "accde"],
    icon: OfficeAccessIcon,
  },
  {
    value: ["file", "pub"],
    icon: OfficePublisherIcon,
  },
  {
    value: ["pdf", "xps"],
    icon: PdfIcon,
  },
  {
    value: ["list", "table", "my_space", "json", "xml", "yaml", "yml"],
    icon: ListViewIcon,
  },
];

/**
 * Extracts the file extension from the filename.
 * Handles cases with or without multiple dots.
 *
 * Examples:
 * - "file.name.doc" => "doc"
 * - "file.name"     => "name"
 * - "file"          => "file"
 *
 * @param {string} filename - The filename from which to extract the extension
 * @returns {string} - The lowercase file extension
 */
const getFileExtension = (filename) => {
  return filename.split(".").pop().toLowerCase();
};

/**
 * Renders the appropriate icon based on the file type or extension.
 *
 * - If `type` is "folder", returns FolderIcon.
 * - If `type` is "table", returns ListViewIcon.
 * - Otherwise, uses the file extension to determine which icon to render.
 * - Falls back to OfficeDocIcon if no match is found.
 *
 * @param {Object} props
 * @param {string} props.type - The type of the item (e.g., "folder", "table", or undefined)
 * @param {string} [props.filename=""] - The name of the file to determine its extension
 * @param {string} props.className - CSS class to apply to the icon
 * @param {string} props.fill - Fill color to apply to the SVG icon
 *
 * @returns {JSX.Element} The matching icon component
 */
const DocumentIcon = ({ type, filename = "", className, fill = "#FACD26" }) => {
  if (type === "folder") {
    return <FolderIcon fill={fill} className={className} />;
  } else if (type === "table") {
    return <ListViewIcon fill={fill} className={className} />;
  } else {
    const extension = getFileExtension(filename);

    // Find icon configuration that includes the extension
    const iconEntry = DOCUMENT_LIST_ICON?.find((entry) => extension && entry?.value?.includes(extension));

    // Return matching icon or fallback to OfficeDocIcon
    return iconEntry ? <iconEntry.icon className={className} /> : <OfficeDocIcon className={className} />;
  }
};

export default DocumentIcon;
